import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';

import { OverwatchActionsState, useOverwatchActions } from 'api/overwatch';
import { formatSupportDateTime } from 'containers/Orbit/support/supportFormat';

import { OverwatchActionKey, OverwatchActionOriginType } from '../widgets/types';
import { useActionSubmission } from './actionSubmissions';
import {
  actionAvailability,
  failureMessage,
  OverwatchActionAvailability
} from './overwatchActions';
import useSecondsUntil from './useSecondsUntil';

export interface IOverwatchActionAvailability {
  availability: OverwatchActionAvailability;
  state: OverwatchActionsState;
  reload: () => void;
}

/**
 * The role gate first, then a 403 the action's submission stored, then discovery. Discovery is
 * fetched only for a role that holds the gate. Every control of an action reads the same answer, so
 * the anomaly button disappears with the wizard it opens.
 */
export const useOverwatchActionAvailability = (
  key: OverwatchActionKey,
  granted: boolean,
  origin?: OverwatchActionOriginType
): IOverwatchActionAvailability => {
  const { state, reload } = useOverwatchActions(granted);
  const { submission } = useActionSubmission(key);
  let availability: OverwatchActionAvailability = 'hidden';

  if (granted && !submission.hidden) availability = actionAvailability(state, key, origin);

  return { availability, state, reload };
};

export type ActionRequestStatus = 'idle' | 'sending' | 'sent' | 'failed';

export interface IOverwatchActionRequest {
  availability: OverwatchActionAvailability;
  status: ActionRequestStatus;
  /** The last failure, for the inline alert next to the button. */
  error: string | null;
  /** When a sent request was submitted: the 201's `submitted_at`, or else the client's clock. */
  sentOn: string | null;
  send: () => void;
}

/**
 * A one-click action with no input. The submission lives in the page-session store, so it survives
 * a tab switch. A retryable failure keeps the `client_request_id` for the next click; a 50332 keeps
 * the button disabled for its wait; a failure that must not be resent leaves it disabled; a 403
 * hides it and reloads discovery.
 */
export const useOverwatchActionRequest = (
  key: OverwatchActionKey,
  granted: boolean
): IOverwatchActionRequest => {
  const { availability, reload } = useOverwatchActionAvailability(key, granted);
  const { submission, submit, update } = useActionSubmission(key);
  const secondsLeft = useSecondsUntil(submission.retryAt);
  // A failure already on screen before this mount is not toasted again.
  const toasted = useRef(submission.failure);

  useEffect(() => {
    if (!submission.discoveryStale) return;

    update({ discoveryStale: false });
    reload();
  }, [submission.discoveryStale, update, reload]);

  useEffect(() => {
    if (submission.failure && submission.failure !== toasted.current) {
      message.error(submission.failure.message);
    }
    toasted.current = submission.failure;
  }, [submission.failure]);

  const { failure } = submission;
  const sentAt = submission.result?.submitted_at ?? submission.sentAt;
  const failed = submission.status === 'failed' && failure !== null;
  let status: ActionRequestStatus = 'idle';

  if (submission.status === 'sending') {
    status = 'sending';
  } else if (submission.status === 'succeeded') {
    status = 'sent';
  } else if (failed && (!failure.retryable || secondsLeft > 0)) {
    status = 'failed';
  }

  const send = useCallback(() => {
    if (availability === 'enabled' && status === 'idle') submit({});
  }, [availability, status, submit]);

  return {
    availability,
    status,
    error: failed ? failureMessage(failure, submission.retryAt, secondsLeft) : null,
    sentOn: status === 'sent' && sentAt ? formatSupportDateTime(sentAt) : null,
    send
  };
};
